import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { clearMedia } from "../../actions/mediaActions";
import { selectMedia } from "./selectors";

/**
 * Custom hook that manages media upload side effects for the session form.
 *
 * Handles:
 * - Tracking preview URLs via ref for safe cleanup
 * - Revoking object URLs on unmount to prevent memory leaks
 * - Processing uploaded images from Redux store into form state
 * - Processing uploaded audio from Redux store into form state
 * - Clearing media from Redux store when all uploads complete
 *
 * @param {Object} formData - The session form data state
 * @param {Function} setFormData - The session form data state setter
 */
const useSessionFormMedia = (formData, setFormData) => {
  const dispatch = useDispatch();
  const { image, audio } = useSelector(selectMedia);

  // Track preview URLs for proper cleanup using ref to avoid closure issues
  const previewUrlsRef = useRef({
    session_image_preview: "",
    session_main_url_preview: "",
    session_text_image_url_preview: "",
  });

  // Update ref whenever preview URLs change
  useEffect(() => {
    previewUrlsRef.current = {
      session_image_preview: formData.session_image_preview,
      session_main_url_preview: formData.session_main_url_preview,
      session_text_image_url_preview: formData.session_text_image_url_preview,
    };
  }, [formData.session_image_preview, formData.session_main_url_preview, formData.session_text_image_url_preview]);

  // Cleanup preview URLs on component unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      // Cleanup all current preview URLs when component unmounts
      if (previewUrlsRef.current.session_image_preview) {
        URL.revokeObjectURL(previewUrlsRef.current.session_image_preview);
      }
      if (previewUrlsRef.current.session_main_url_preview) {
        URL.revokeObjectURL(previewUrlsRef.current.session_main_url_preview);
      }
      if (previewUrlsRef.current.session_text_image_url_preview) {
        URL.revokeObjectURL(previewUrlsRef.current.session_text_image_url_preview);
      }
    };
  }, []); // Empty dependency array is correct here since we use ref

  // Handle uploaded media from Redux store
  useEffect(() => {
    if (image && (formData.session_image_uploading || formData.session_text_image_url_uploading)) {
      if (formData.session_image_uploading) {
        // Revoke the preview URL to prevent memory leak
        if (formData.session_image_preview) {
          URL.revokeObjectURL(formData.session_image_preview);
        }

        setFormData(prev => ({
          ...prev,
          session_image: image,
          session_image_uploading: false,
          session_image_preview: "", // Clear preview after successful upload
        }));
      } else if (formData.session_text_image_url_uploading) {
        // Revoke the preview URL to prevent memory leak
        if (formData.session_text_image_url_preview) {
          URL.revokeObjectURL(formData.session_text_image_url_preview);
        }

        setFormData(prev => ({
          ...prev,
          session_text_image_url: image,
          session_text_image_url_uploading: false,
          session_text_image_url_preview: "", // Clear preview after successful upload
        }));
      }
    }
  }, [
    image,
    formData.session_image_uploading,
    formData.session_text_image_url_uploading,
    formData.session_image_preview,
    formData.session_text_image_url_preview,
    dispatch,
  ]);

  // Handle uploaded audio from Redux store
  useEffect(() => {
    if (audio && formData.session_main_url_uploading) {
      // Revoke the preview URL to prevent memory leak
      if (formData.session_main_url_preview) {
        URL.revokeObjectURL(formData.session_main_url_preview);
      }

      setFormData(prev => ({
        ...prev,
        session_main_url: audio,
        session_main_url_uploading: false,
        session_main_url_preview: "", // Clear preview after successful upload
      }));
    }
  }, [audio, formData.session_main_url_uploading, formData.session_main_url_preview, dispatch]);

  // Separate effect to monitor upload states and clear media when all uploads complete
  useEffect(() => {
    // Check if we have media in the store but no uploads are active
    const hasMediaToProcess = (image && image !== "") || (audio && audio !== "");
    const noUploadsActive =
      !formData.session_main_url_uploading &&
      !formData.session_image_uploading &&
      !formData.session_text_image_url_uploading;

    if (hasMediaToProcess && noUploadsActive) {
      // Add a small delay to ensure state updates have completed
      const timeoutId = setTimeout(() => {
        dispatch(clearMedia());
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [
    formData.session_main_url_uploading,
    formData.session_image_uploading,
    formData.session_text_image_url_uploading,
    image,
    audio,
    dispatch,
  ]);
};

export default useSessionFormMedia;
